import React, { useState, useEffect, useRef, useCallback } from 'react';
import './WelcomeScreen.css';
import { userApi, gameApi, prizeApi } from '../api';
import { useNavigate } from 'react-router-dom';
import { Prize } from '../types/prizes';
import { Category, OptionalQuestions } from '../types/game';
import ConsentModal from './ConsentModal';

const WelcomeScreen: React.FC = () => {
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [existingUser, setExistingUser] = useState<{username: string, level: string} | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('Hackers & Threats');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [userEarnedPrizes, setUserEarnedPrizes] = useState<Prize[]>([]);
  const [newUserRegistered, setNewUserRegistered] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [optionalQuestions, setOptionalQuestions] = useState<OptionalQuestions>({
    experience_level: '',
    years_of_experience: '',
    country: '',
    other_country: ''
  });
  const [showExperience, setShowExperience] = useState(false);
  const [showDemographics, setShowDemographics] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [showOptionalQuestions, setShowOptionalQuestions] = useState(true);
  const [privacyNoticeAccepted, setPrivacyNoticeAccepted] = useState(false);
  // New state for storing consent data before registration
  const [pendingConsent, setPendingConsent] = useState<{consented: boolean, privacy_notice_accepted: boolean} | null>(null);
  const [showConsentForNewUser, setShowConsentForNewUser] = useState(false);

  const experienceLevels = [
    '1 - Basic Awareness',
    '2 - Novice',
    '3 - Intermediate',
    '4 - Advanced',
    '5 - Expert'
  ];

  const countries = [
    'USA',
    'India',
    'Russia',
    'Germany',
    'Canada',
    'United Kingdom',
    'Sweden',
    'Netherlands',
    'China',
    'Australia',
    'Other',
    'Prefer not to answer'
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!Object.values(dropdownRefs.current).some(ref => ref?.contains(event.target as Node))) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const checkExistingUser = async () => {
      try {
        const response = await userApi.getProfile() 
        if (response.data.status === 'success' && response.data.exists) {
          setExistingUser(response.data.user);
          setShowConsent(!response.data.user.answered_consent_popup || !response.data.user.privacy_notice_accepted);
          setUsername(response.data.user.username);
          setShowOptionalQuestions(!response.data.user.answered_optional_questions);
          setPrivacyNoticeAccepted(response.data.user.privacy_notice_accepted || false);
        } else {
          // New user - show consent modal first
          setShowConsentForNewUser(true);
        }
      } catch (err) {
        console.error('Error checking user:', err);
        setAuthError(true);
        setError('Failed to load user profile. Please exit the app and try again.');
      } finally {
        setIsLoading(false);
      }
    };

    checkExistingUser();
  }, [newUserRegistered]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await userApi.getAvailableCategories();
        if (response.data.status === 'success') {
          setCategories(response.data.categories);
          // Find the first non-completed category
          const firstNonCompletedCategory = response.data.categories.find((category: Category) => !category.isCompleted);
          if (firstNonCompletedCategory) {
            setSelectedCategory(firstNonCompletedCategory.name);
          } else {
            // If all categories are completed, default to the first one
            setSelectedCategory(response.data.categories[0].name);
          }
        }
      } catch (err) {
        console.error('Failed to load categories');
        setCategories([{
          name: "Hackers & Threats"
        }]);
        setSelectedCategory("Hackers & Threats");
      }
    };

    const getConsentForNewUser = () => {
      if (newUserRegistered) {
        setShowConsent(true);
      }
    }

    fetchCategories();
    getConsentForNewUser();
  }, [newUserRegistered]);

  useEffect(() => {
    const fetchPrizes = async () => {
      try {
        const allPrizesResponse = await prizeApi.getAllPrizes();
        if (allPrizesResponse.data.status === 'success') {
          setPrizes(allPrizesResponse.data.prizes);
        }        
        const userPrizesResponse = await prizeApi.getUserPrizes();
        if (userPrizesResponse.data.status === 'success') {
          setUserEarnedPrizes(userPrizesResponse.data.earned_prizes);
        }
      } catch (err) {
        console.error('Failed to load prizes');
      }
    };

    fetchPrizes();
  }, []);

  const handleCategoryChange = async (categoryId: string) => {
    setSelectedCategory(categoryId);
    const response = await userApi.updateCategory(categoryId);
    if (response.data.status === 'success') {
      setIsDropdownOpen(false);
    } else {
      setError('Failed to update category');
    }
  };

  const selectedCategoryName = categories.find(cat => cat.name === selectedCategory)?.name || (categories[0] ? categories[0].name : "Hackers & Threats");

  // Sort categories with completed ones at the bottom
  const sortedCategories = [...categories].sort((a, b) => {
    if (a.isCompleted === b.isCompleted) return 0;
    return a.isCompleted ? 1 : -1;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) {
      setError('Please enter your name');
      return;
    }

    try {
      setError(null);
      if (!existingUser) {
        // For new users, check if consent was provided
        if (!pendingConsent) {
          setError('Please complete the consent form before registering');
          setShowConsentForNewUser(true);
          return;
        }
        
        // For new users, include consent data in registration
        const hasOptionalQuestions = Object.values(optionalQuestions).some(value => value !== '');
        const response = await userApi.register(
          username,
          hasOptionalQuestions ? optionalQuestions : undefined,
          pendingConsent
        );
        setNewUserRegistered(true);
        return;
      }

      // If user exists and has optional questions to update
      if (showOptionalQuestions) {
        const hasOptionalQuestions = Object.values(optionalQuestions).some(value => value !== '');
        if (hasOptionalQuestions) {
          await userApi.updateOptionalQuestions(optionalQuestions);
          setShowOptionalQuestions(false);
        }
      }

      await handleStartGame(selectedCategory, username);
    } catch (err: any) {
      setError(err.response.data.message);
    }
  };

  const handleStartGame = useCallback(async (category: string, username: string) => {
    try {
      setLoading(true);
      navigate('/play', { state: { selectedCategory: category, username: username } });
    } catch (error) {
      console.error('Failed to start game:', error);
      setError('Failed to start game. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const renderCustomDropdown = (
    id: string,
    value: string,
    options: string[],
    onChange: (value: string) => void,
    placeholder: string
  ) => (
    <div className={`custom-select ${openDropdown === id ? 'open' : ''}`} ref={el => dropdownRefs.current[id] = el}>
      <div 
        className="selected-option"
        onClick={() => setOpenDropdown(openDropdown === id ? null : id)}
      >
        {value || placeholder}
        <span className="dropdown-arrow">▼</span>
      </div>
      {openDropdown === id && (
        <div className="options-list">
          {options.map(option => (
            <div
              key={option}
              className={`option ${option === value ? 'selected' : ''}`}
              onClick={() => {
                onChange(option);
                setOpenDropdown(null);
              }}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return <div className="welcome-container">Loading...</div>;
  }

  const handleConsentSubmit = (consented: boolean, privacy_notice_accepted: boolean) => {
    setPendingConsent({ consented, privacy_notice_accepted });
    setPrivacyNoticeAccepted(privacy_notice_accepted);
    setShowConsentForNewUser(false);
  };

  return (
    <div className="welcome-container">
      <div className="welcome-screen">
        <div className="rsac-logo-container">
          <img 
            src="/RSAC_Conference_logo.png" 
            alt="RSAC Conference" 
            className="rsac-logo"
          />
        </div>
        
        <div className="game-title">
          <div className="title">AI Showdown:</div>
          <div className="subtitle">Security Expertise</div>
        </div>
        
        <div className="welcome-story">
          <p>
            🤖 The AIs have become overconfident. They think they're security experts, 
            ready to replace human intuition.
          </p>
          <p>
            It's time to prove them wrong! Show these machines that human 
            intuition still triumphs.
          </p>
          <p>
            <strong>🔐 Ready to take your expertise to the next level?</strong> <a href="https://portal.onersac.com/" target="_blank" rel="noopener noreferrer">Join <strong>RSAC™ Membership</strong></a>—now free* for a limited time.
          </p>
        </div>
        
        {/* <div className="welcome-prizes-section">
          <h3>Available Prizes</h3>
          <div className="welcome-prize-items">
            {prizes.map((prize) => (
              <div key={prize.id} className={`welcome-prize-item ${userEarnedPrizes.some(p => p.id === prize.id) ? '' : 'locked'}`}>
                <div className="welcome-prize-icon">{prize.icon}</div>
                <div className="welcome-prize-details">
                  <div className="welcome-prize-name">{prize.name}</div>
                  <div className="welcome-prize-condition">{prize.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div> */}

        <form onSubmit={handleSubmit} className="welcome-form">
          <div className="form-group">
            {existingUser ? (
              <div className="welcome-back-message">
                Welcome {newUserRegistered ? "back" : ""}, <span className="username-highlight">{existingUser.username}</span>!
                <div className="level-info">Level: {existingUser.level}</div>
                <div className="challenge-prompt">Ready for {newUserRegistered ? "another" : "a"} challenge?</div>
              </div>
            ) : (
              <>
                <label htmlFor="username">Enter your username, security expert:</label>
                <input
                  type="text"
                  id="username"
                  name="game_username_unique"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Your username"
                  required
                  autoComplete="off"
                  spellCheck="false"
                />
                {!pendingConsent && (
                  <div className="consent-required-message">
                    Please complete the consent form above before registering.
                  </div>
                )}
              </>
            )}
          </div>

          {showOptionalQuestions && (
            <div className="optional-questions">
              <h3>Tell us about yourself</h3>
              <div className="form-group">
                <label>How would you assess your skill level as a cybersecurity professional?</label>
                {renderCustomDropdown(
                  'experience_level',
                  optionalQuestions.experience_level,
                  experienceLevels,
                  (value) => setOptionalQuestions({ ...optionalQuestions, experience_level: value }),
                  'Select Experience Level'
                )}
              </div>

              <div className="form-group">
                <label>How many years of experience do you have working in cybersecurity?</label>
                <input
                  type="number"
                  value={optionalQuestions.years_of_experience}
                  onChange={(e) => setOptionalQuestions({
                    ...optionalQuestions,
                    years_of_experience: e.target.value
                  })}
                  placeholder="Enter years of experience"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>In which country do you currently reside?</label>
                {renderCustomDropdown(
                  'country',
                  optionalQuestions.country,
                  countries,
                  (value) => setOptionalQuestions({ ...optionalQuestions, country: value }),
                  'Select Country'
                )}
                {optionalQuestions.country === 'Other' && (
                  <input
                    type="text"
                    value={optionalQuestions.other_country || ''}
                    onChange={(e) => setOptionalQuestions({
                      ...optionalQuestions,
                      other_country: e.target.value
                    })}
                    placeholder="Please specify your country"
                    className="other-country-input"
                  />
                )}
              </div>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
          
          {!existingUser && !authError ? (
            <button type="submit" className="start-button-register" disabled={loading || !pendingConsent}>
              {loading ? 'Loading...' : 'REGISTER'}
            </button>
          ) : (
            <>
              <div className="form-group category-select" ref={dropdownRef}>
                <label>Select your challenge:</label>
                <div 
                  className={`custom-select ${isDropdownOpen ? 'open' : ''}`}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <div className="selected-option">
                    {selectedCategoryName}
                    <span className="dropdown-arrow">▼</span>
                  </div>
                  {isDropdownOpen && (
                    <div className="options-list">
                      {sortedCategories.map(category => (
                        <div
                          key={category.name}
                          className={`option ${category.name === selectedCategory ? 'selected' : ''} ${category.isCompleted ? 'completed' : ''}`}
                          onClick={() => !category.isCompleted && handleCategoryChange(category.name)}
                        >
                          {category.name}
                          {category.isCompleted && <span className="completion-badge">✓</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {!privacyNoticeAccepted && (
                <div className="privacy-notice-warning">
                  Please accept the Privacy Notice to continue. You can review it in the consent popup.
                </div>
              )}
              <button type="submit" className="start-button" disabled={loading || !privacyNoticeAccepted}>
                {loading ? 'Loading...' : 'CHALLENGE THE AIs'}
              </button>
            </>
          )}
        </form>
        
        <div className="rsac-footer">
          Created by{' '}
          <a href="https://www.onersac.com/rsac-labs" target="_blank" rel="noopener noreferrer">
            RSAC™ Labs
          </a>
        </div>
      </div>
      {showConsentForNewUser && (
        <ConsentModal 
          onClose={() => setShowConsentForNewUser(false)}
          onSubmit={handleConsentSubmit}
        />
      )}
      {showConsent && (
        <ConsentModal 
          onClose={() => setShowConsent(false)}
          onPrivacyNoticeAccepted={() => setPrivacyNoticeAccepted(true)}
        />
      )}
    </div>
  );
};

export default WelcomeScreen; 